import React, { useState, useEffect } from 'react';
import { X, ArrowDownCircle, ArrowUpCircle, Gift, Waves } from 'lucide-react';
import { analyticsApi } from '../../services/api';
import { WavesLedgerData, WavesLedgerEntry } from '../../types';
import styles from './WavesLedgerModal.module.css';

interface WavesLedgerModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
}

type TabType = 'received' | 'given' | 'redeemed';

export const WavesLedgerModal: React.FC<WavesLedgerModalProps> = ({
  isOpen,
  onClose,
  userId,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('received');
  const [data, setData] = useState<WavesLedgerData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchLedger();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, userId]);

  const fetchLedger = async () => {
    try {
      setLoading(true);
      const response = await analyticsApi.getWavesLedger(userId);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch waves ledger:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderTransactions = (transactions: WavesLedgerEntry[], type: TabType) => {
    if (transactions.length === 0) {
      return (
        <div className={styles.emptyState}>
          <p>No transactions yet</p>
        </div>
      );
    }

    return (
      <div className={styles.transactionList}>
        {transactions.map((transaction) => (
          <div key={transaction.id} className={styles.transactionItem}>
            <div className={styles.transactionIcon}>
              {type === 'received' && <ArrowDownCircle size={20} className={styles.iconReceived} />}
              {type === 'given' && <ArrowUpCircle size={20} className={styles.iconGiven} />}
              {type === 'redeemed' && <Gift size={20} className={styles.iconRedeemed} />}
            </div>
            <div className={styles.transactionDetails}>
              <p className={styles.transactionDescription}>
                {type === 'received' && transaction.first_name && (
                  <>From <strong>{transaction.first_name} {transaction.last_name}</strong></>
                )}
                {type === 'given' && transaction.first_name && (
                  <>To <strong>{transaction.first_name} {transaction.last_name}</strong></>
                )}
                {type === 'redeemed' && transaction.reward_name && (
                  <>Redeemed <strong>{transaction.reward_name}</strong></>
                )}
                {!transaction.first_name && !transaction.reward_name && (
                  <>{transaction.description || 'Transaction'}</>
                )}
              </p>
              <span className={styles.transactionDate}>{formatDate(transaction.created_at)}</span>
            </div>
            <div className={`${styles.transactionAmount} ${styles[type]}`}>
              {type === 'received' ? '+' : '-'}{Math.abs(transaction.amount)} waves
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <Waves size={24} />
            <h2>Waves Ledger</h2>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <p>Loading transactions...</p>
          </div>
        ) : data ? (
          <>
            <div className={styles.summary}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Total Received</span>
                <span className={`${styles.summaryValue} ${styles.received}`}>
                  +{data.summary.total_received}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Total Given</span>
                <span className={`${styles.summaryValue} ${styles.given}`}>
                  -{data.summary.total_given}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Total Redeemed</span>
                <span className={`${styles.summaryValue} ${styles.redeemed}`}>
                  -{data.summary.total_redeemed}
                </span>
              </div>
            </div>

            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${activeTab === 'received' ? styles.active : ''}`}
                onClick={() => setActiveTab('received')}
              >
                <ArrowDownCircle size={16} />
                Received ({data.received.length})
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'given' ? styles.active : ''}`}
                onClick={() => setActiveTab('given')}
              >
                <ArrowUpCircle size={16} />
                Given ({data.given.length})
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'redeemed' ? styles.active : ''}`}
                onClick={() => setActiveTab('redeemed')}
              >
                <Gift size={16} />
                Redeemed ({data.redeemed.length})
              </button>
            </div>

            <div className={styles.content}>
              {activeTab === 'received' && renderTransactions(data.received, 'received')}
              {activeTab === 'given' && renderTransactions(data.given, 'given')}
              {activeTab === 'redeemed' && renderTransactions(data.redeemed, 'redeemed')}
            </div>
          </>
        ) : (
          <div className={styles.error}>
            <p>Failed to load data</p>
          </div>
        )}
      </div>
    </div>
  );
};
